import { useState, useEffect, useCallback } from 'react';
import { QuotationRequest } from '../types';
import { BillData, Company, PaymentRecord, LedgerSummary, ProfitLossPoint, DeliveryChallan } from '../types/billing';
import {
  listCompanies,
  createCompany,
  createBill,
  listBillsByCompany,
  listAllBills,
  updateBill as firebaseUpdateBill,
  deleteBill as firebaseDeleteBill,
  recordPayment,
  listPaymentsByCompany,
  listAllPayments,
  updatePayment as firebaseUpdatePayment,
  deletePayment as firebaseDeletePayment,
  listAllQuotations,
  createDeliveryChallan,
  listAllDeliveryChallans,
  updateDeliveryChallan,
  deleteDeliveryChallan,
  markChallanAsReturned,
  markChallanAsOverdue,
  markChallanAsLost,
  generateNextChallanNumber
} from '../services/firebaseBilling';
import { db } from '../config/firebase';

export interface AdminState {
  // Data
  quotations: QuotationRequest[];
  bills: BillData[];
  companies: Company[];
  payments: PaymentRecord[];
  deliveryChallans: DeliveryChallan[];
  
  // UI State
  isLoading: boolean;
  activeTab: 'quotations' | 'bills' | 'companies' | 'deliveryChallans' | 'messages' | 'payments' | 'ledger' | 'analytics' | 'reports';
  filterStatus: string;

  // Bill filtering
  billDateFrom: string;
  billDateTo: string;
  billStatusFilter: string;
  
  // Modal States
  showBillGenerator: boolean;
  showCompanyManager: boolean;
  showPaymentForm: boolean;
  showUtReportForm: boolean;
  showUtReport: boolean;
  showDeliveryChallanForm: boolean;
  showDeliveryChallanManagement: boolean;
  
  // Selected Items
  selectedQuotation: QuotationRequest | null;
  selectedUtReport: any | null;
  selectedCompanyForPayment: string | undefined;
  selectedCompanyForLedger: string;
  detailsCompanyId: string | null;
  editingCompany: Company | null;
  selectedDeliveryChallan: DeliveryChallan | null;
  
  // Form Data
  
  // Analytics
  analyticsRange: 'monthly' | 'yearly';
  analyticsCompany: string;
  ledgerSummary: LedgerSummary;
  
  // PDF Generation
  isGeneratingPDF: boolean;
  isSavingBill: boolean;
  isSavingCompany: boolean;
  isSavingPayment: boolean;
  isSavingUtReport: boolean;
}

export interface AdminActions {
  // Data Actions
  loadData: () => Promise<void>;
  refreshData: () => Promise<void>;
  
  // Tab Actions
  setActiveTab: (tab: AdminState['activeTab']) => void;
  setFilterStatus: (status: string) => void;
  
  // Modal Actions
  openModal: (modal: 'showBillGenerator' | 'showCompanyManager' | 'showPaymentForm' | 'showUtReportForm' | 'showUtReport' | 'showDeliveryChallanForm' | 'showDeliveryChallanManagement') => void;
  closeModal: (modal: 'showBillGenerator' | 'showCompanyManager' | 'showPaymentForm' | 'showUtReportForm' | 'showUtReport' | 'showDeliveryChallanForm' | 'showDeliveryChallanManagement') => void;
  
  // Selection Actions
  selectQuotation: (quotation: QuotationRequest | null) => void;
  selectUtReport: (report: any | null) => void;
  selectCompanyForPayment: (companyId: string | undefined) => void;
  selectCompanyForLedger: (companyId: string) => void;
  selectCompanyDetails: (companyId: string | null) => void;
  editCompany: (company: Company | null) => void;
  selectDeliveryChallan: (challan: DeliveryChallan | null) => void;
  
  // Form Actions
  
  // Business Actions
  saveBill: (billData: BillData) => Promise<void>;
  updateBill: (billData: BillData) => Promise<void>;
  deleteBill: (billId: string) => Promise<void>;
  saveCompany: (company: Company) => Promise<void>;
  updateCompany: (company: Company) => Promise<void>;
  deleteCompany: (companyId: string) => Promise<void>;
  savePayment: (payment: PaymentRecord) => Promise<void>;
  addPayment: (payment: Omit<PaymentRecord, 'id'>) => Promise<void>;
  saveDeliveryChallan: (challan: Omit<DeliveryChallan, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateDeliveryChallanAction: (challan: DeliveryChallan) => Promise<void>;
  deleteDeliveryChallanAction: (challanId: string) => Promise<void>;
  markDeliveryChallanAsReturned: (challanId: string, returnCondition?: string, notes?: string) => Promise<void>;
  markDeliveryChallanAsOverdue: (challanId: string) => Promise<void>;
  markDeliveryChallanAsLost: (challanId: string, notes?: string) => Promise<void>;
  updatePayment: (payment: PaymentRecord) => Promise<void>;
  deletePayment: (paymentId: string) => Promise<void>;
  saveUtReport: (report: any) => Promise<void>;
  
  // Analytics Actions
  setAnalyticsRange: (range: 'monthly' | 'yearly') => void;
  setAnalyticsCompany: (companyId: string) => void;
  computeAnalyticsData: () => ProfitLossPoint[];

  // Bill filtering actions
  setBillDateFrom: (date: string) => void;
  setBillDateTo: (date: string) => void;
  setBillStatusFilter: (status: string) => void;

  // Bill filtering helpers
  getFilteredBills: () => BillData[];
}

export const useAdminState = (): AdminState & AdminActions => {
  // Data State
  const [quotations, setQuotations] = useState<QuotationRequest[]>([]);
  const [bills, setBills] = useState<BillData[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [deliveryChallans, setDeliveryChallans] = useState<DeliveryChallan[]>([]);
  
  // UI State
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<AdminState['activeTab']>('quotations');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // Bill filtering
  const [billDateFrom, setBillDateFrom] = useState<string>('');
  const [billDateTo, setBillDateTo] = useState<string>('');
  const [billStatusFilter, setBillStatusFilter] = useState<string>('all');
  
  // Modal States
  const [showBillGenerator, setShowBillGenerator] = useState(false);
  const [showCompanyManager, setShowCompanyManager] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showUtReportForm, setShowUtReportForm] = useState(false);
  const [showUtReport, setShowUtReport] = useState(false);
  const [showDeliveryChallanForm, setShowDeliveryChallanForm] = useState(false);
  const [showDeliveryChallanManagement, setShowDeliveryChallanManagement] = useState(false); // ✅ Fixed: distinct names
  
  // Selected Items
  const [selectedQuotation, setSelectedQuotation] = useState<QuotationRequest | null>(null);
  const [selectedUtReport, setSelectedUtReport] = useState<any | null>(null);
  const [selectedCompanyForPayment, setSelectedCompanyForPayment] = useState<string | undefined>();
  const [selectedCompanyForLedger, setSelectedCompanyForLedger] = useState<string>('all');
  const [detailsCompanyId, setDetailsCompanyId] = useState<string | null>(null);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [selectedDeliveryChallan, setSelectedDeliveryChallan] = useState<DeliveryChallan | null>(null);
  
  // Form Data
  
  // Analytics
  const [analyticsRange, setAnalyticsRange] = useState<'monthly' | 'yearly'>('monthly');
  const [analyticsCompany, setAnalyticsCompany] = useState<string>('all');
  const [ledgerSummary, setLedgerSummary] = useState<LedgerSummary>({
    periodLabel: 'As on date',
    accounts: []
  });
  
  // PDF Generation States
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isSavingBill, setIsSavingBill] = useState(false);
  const [isSavingCompany, setIsSavingCompany] = useState(false);
  const [isSavingPayment, setIsSavingPayment] = useState(false);
  const [isSavingUtReport, setIsSavingUtReport] = useState(false);

  // Load all data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      console.log('Loading data...');
      
      const [companiesData, paymentsData, billsData, quotationsData, deliveryChallansData] = await Promise.all([
        listCompanies(),
        listAllPayments(),
        listAllBills(),
        listAllQuotations(),
        listAllDeliveryChallans()
      ]);
      
      console.log('Companies loaded:', companiesData.length);
      console.log('Payments loaded:', paymentsData.length);
      console.log('Bills loaded:', billsData.length);
      console.log('Delivery Challans loaded:', deliveryChallansData.length);

      setCompanies(companiesData);
      setPayments(paymentsData);
      setBills(billsData);
      setDeliveryChallans(deliveryChallansData);

      setQuotations(quotationsData);
      console.log('Quotations loaded:', quotationsData.length);

      console.log('Data loading completed successfully');
    } catch (error) {
      console.error('Error loading data:', error);
      // Set empty arrays on error to prevent undefined issues
      setCompanies([]);
      setPayments([]);
      setBills([]);
      setDeliveryChallans([]);
      setQuotations([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Refresh data without loading state
  const refreshData = useCallback(async () => {
    try {
      await loadData();
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  }, [loadData]);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Calculate ledger data
  useEffect(() => {
    const calculateLedgerData = () => {
      console.log('Calculating ledger data...', { 
        billsCount: bills.length, 
        paymentsCount: payments.length, 
        selectedCompany: selectedCompanyForLedger 
      });
      
      // Filter bills and payments based on selected company
      const filteredBills = selectedCompanyForLedger === 'all' 
        ? bills 
        : bills.filter(bill => bill.clientId === selectedCompanyForLedger);
      
      const filteredPayments = selectedCompanyForLedger === 'all' 
        ? payments 
        : payments.filter(payment => payment.companyId === selectedCompanyForLedger);
      
      console.log('Filtered data:', { 
        filteredBillsCount: filteredBills.length, 
        filteredPaymentsCount: filteredPayments.length 
      });
      
      // Calculate totals with proper error handling
      const totalBilled = filteredBills.reduce((sum, bill) => {
        const amount = bill.totalAmount || 0;
        if (isNaN(amount)) {
          console.warn('Invalid bill amount:', bill);
          return sum;
        }
        return sum + amount;
      }, 0);
      
      const totalReceived = filteredPayments.reduce((sum, payment) => {
        const amount = payment.amount || 0;
        if (isNaN(amount)) {
          console.warn('Invalid payment amount:', payment);
          return sum;
        }
        return sum + amount;
      }, 0);
      
      const outstandingAmount = totalBilled - totalReceived;

      console.log('Ledger calculations:', { totalBilled, totalReceived, outstandingAmount });

      // Get company name for period label
      const companyName = selectedCompanyForLedger === 'all' 
        ? 'All Companies' 
        : companies.find(c => c.id === selectedCompanyForLedger)?.name || 'Selected Company';

      console.log('Company name:', companyName);
      
      // Create detailed transactions from bills and payments
      // Group by financial year (Apr to Mar) or periods
      const allTransactions: Array<{
        date?: string;
        particulars: string;
        vchType?: string;
        vchNo?: string;
        debit?: number;
        credit?: number;
        isSubtotal?: boolean;
        isClosingBalance?: boolean;
        isOpeningBalance?: boolean;
      }> = [];

      // Create a map of bills by ID for quick lookup
      const billsMap = new Map(filteredBills.map(bill => [bill.id, bill]));

      // Add bill transactions (Sales entries - shown in Debit column)
      filteredBills.forEach(bill => {
        const roundedAmount = Math.round(bill.totalAmount);
        allTransactions.push({
          date: bill.invoiceDate,
          particulars: `Cr Sales @ ${bill.gstRate}%`,
          vchType: 'Sales',
          vchNo: bill.invoiceNumber,
          debit: roundedAmount,
          credit: undefined
        });
      });

      // Add payment transactions (shown in Credit column to reduce outstanding)
      filteredPayments.forEach(payment => {
        const roundedAmount = Math.round(payment.amount);
        // Find the associated bill to get the invoice number
        const associatedBill = payment.billId ? billsMap.get(payment.billId) : null;
        const vchNo = associatedBill?.invoiceNumber || payment.reference || payment.id.substring(0, 8);
        
        allTransactions.push({
          date: payment.date,
          particulars: `Dr ${payment.mode} Payment`,
          vchType: 'Payment',
          vchNo: vchNo,
          debit: undefined,
          credit: roundedAmount
        });
      });

      // Sort all transactions by date (only those with dates)
      allTransactions.sort((a, b) => {
        if (!a.date || !b.date) return 0;
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });

      // Group transactions by financial year (Apr to Mar)
      const groupedByPeriod: { [key: string]: typeof allTransactions } = {};
      
      allTransactions.forEach(trans => {
        if (!trans.date) return; // Skip transactions without dates (shouldn't happen for bills/payments)
        const date = new Date(trans.date);
        const year = date.getFullYear();
        const month = date.getMonth() + 1; // 1-12
        
        // Financial year: Apr (month 4) to Mar (month 3)
        let fyStart: number;
        let fyEnd: number;
        let periodKey: string;
        
        if (month >= 4) {
          // Apr to Dec of current year
          fyStart = year;
          fyEnd = year + 1;
          periodKey = `FY${fyStart}-${fyEnd}`;
        } else {
          // Jan to Mar of current year (belongs to previous FY)
          fyStart = year - 1;
          fyEnd = year;
          periodKey = `FY${fyStart}-${fyEnd}`;
        }
        
        if (!groupedByPeriod[periodKey]) {
          groupedByPeriod[periodKey] = [];
        }
        groupedByPeriod[periodKey].push(trans);
      });

      // Build final transaction list with subtotals and closing/opening balances
      const transactions: typeof allTransactions = [];
      const periods = Object.keys(groupedByPeriod).sort();
      let previousClosingBalance = 0;

      periods.forEach((periodKey, periodIndex) => {
        const periodTransactions = groupedByPeriod[periodKey];
        
        // Add opening balance for periods after the first
        if (periodIndex > 0 && previousClosingBalance > 0) {
          transactions.push({
            date: undefined,
            particulars: 'Cr Opening Balance',
            vchType: undefined,
            vchNo: undefined,
            debit: previousClosingBalance,
            credit: undefined,
            isOpeningBalance: true
          });
        }

        // Add all transactions for this period
        transactions.push(...periodTransactions);

        // Calculate subtotal for this period
        const periodDebitTotal = periodTransactions.reduce((sum, t) => sum + (t.debit || 0), 0);
        const periodCreditTotal = periodTransactions.reduce((sum, t) => sum + (t.credit || 0), 0);
        const periodNetDebit = periodDebitTotal - periodCreditTotal;
        
        // Add opening balance to net if present
        const periodStartBalance = periodIndex > 0 ? previousClosingBalance : 0;
        const periodTotalDebit = periodStartBalance + periodDebitTotal;

        // Add subtotal row
        transactions.push({
          date: undefined,
          particulars: '',
          vchType: undefined,
          vchNo: undefined,
          debit: periodTotalDebit,
          credit: undefined,
          isSubtotal: true
        });

        // Add closing balance credit side
        transactions.push({
          date: undefined,
          particulars: '',
          vchType: undefined,
          vchNo: undefined,
          debit: undefined,
          credit: periodTotalDebit,
          isClosingBalance: true
        });

        // Add final closing balance row (both sides)
        const closingBalance = periodTotalDebit;
        previousClosingBalance = closingBalance;
        transactions.push({
          date: undefined,
          particulars: 'Dr Closing Balance',
          vchType: undefined,
          vchNo: undefined,
          debit: closingBalance,
          credit: closingBalance,
          isClosingBalance: true
        });
      });

      // Create ledger accounts
      const accounts = [
        {
          ledgerName: 'Sales A/c (Billed Amount)',
          debitBalance: 0,
          creditBalance: Math.round(totalBilled)
        },
        {
          ledgerName: 'Cash/Bank A/c (Received)',
          debitBalance: Math.round(totalReceived),
          creditBalance: 0
        },
        {
          ledgerName: 'Sundry Debtors (Outstanding)',
          debitBalance: Math.max(0, Math.round(outstandingAmount)),
          creditBalance: 0
        }
      ];

      // Calculate date range for period label
      let periodLabel = `Ledger - ${companyName}`;
      if (transactions.length > 0) {
        const datesWithValues = transactions
          .filter(t => t.date)
          .map(t => new Date(t.date!))
          .sort((a, b) => a.getTime() - b.getTime());
        
        if (datesWithValues.length > 0) {
          const startDate = datesWithValues[0];
          const endDate = datesWithValues[datesWithValues.length - 1];
          const formatDateForLabel = (date: Date) => {
            const day = date.getDate();
            const month = date.toLocaleDateString('en-GB', { month: 'short' });
            const year = date.getFullYear().toString().slice(-2);
            return `${day}-${month}-${year}`;
          };
          periodLabel = `${formatDateForLabel(startDate)} to ${formatDateForLabel(endDate)}`;
        }
      }

      setLedgerSummary({
        periodLabel: periodLabel,
        accounts: accounts,
        transactions: transactions
      });
    };

    calculateLedgerData();
  }, [bills, payments, selectedCompanyForLedger, companies]);

  // Calculate analytics data
  const computeAnalyticsData = useCallback((): ProfitLossPoint[] => {
    console.log('Computing analytics data...', { 
      billsCount: bills.length, 
      paymentsCount: payments.length,
      selectedCompany: analyticsCompany 
    });
    
    // Filter bills and payments based on selected company for analytics
    const filteredBills = analyticsCompany === 'all' 
      ? bills 
      : bills.filter(bill => bill.clientId === analyticsCompany);
    
    const filteredPayments = analyticsCompany === 'all' 
      ? payments 
      : payments.filter(payment => payment.companyId === analyticsCompany);
    
    // Group bills and payments by month
    const monthlyData: { [key: string]: { billed: number; received: number } } = {};
    
    // Process bills
    filteredBills.forEach(bill => {
      const date = new Date(bill.invoiceDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { billed: 0, received: 0 };
      }
      
      const amount = bill.totalAmount || 0;
      if (!isNaN(amount)) {
        monthlyData[monthKey].billed += amount;
      }
    });
    
    // Process payments
    filteredPayments.forEach(payment => {
      const date = new Date(payment.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { billed: 0, received: 0 };
      }
      
      const amount = payment.amount || 0;
      if (!isNaN(amount)) {
        monthlyData[monthKey].received += amount;
      }
    });
    
    // Convert to array and sort by period
    const analyticsData = Object.entries(monthlyData)
      .map(([period, data]) => ({
        period,
        billedAmount: data.billed,
        receivedAmount: data.received
      }))
      .sort((a, b) => a.period.localeCompare(b.period));
    
    console.log('Analytics data computed:', analyticsData);
    return analyticsData;
  }, [bills, payments, analyticsCompany]);

  // Bill filtering function
  const getFilteredBills = useCallback((): BillData[] => {
    let filtered = [...bills];

    // Filter by status
    if (billStatusFilter !== 'all') {
      filtered = filtered.filter(bill => bill.status === billStatusFilter);
    }

    // Filter by date range
    if (billDateFrom) {
      const fromDate = new Date(billDateFrom);
      filtered = filtered.filter(bill => {
        const billDate = new Date(bill.invoiceDate);
        return billDate >= fromDate;
      });
    }

    if (billDateTo) {
      const toDate = new Date(billDateTo);
      toDate.setHours(23, 59, 59, 999); // Include the entire day
      filtered = filtered.filter(bill => {
        const billDate = new Date(bill.invoiceDate);
        return billDate <= toDate;
      });
    }

    return filtered;
  }, [bills, billStatusFilter, billDateFrom, billDateTo]);

  // Modal actions
  const openModal = useCallback((modal: 'showBillGenerator' | 'showCompanyManager' | 'showPaymentForm' | 'showUtReportForm' | 'showUtReport' | 'showDeliveryChallanForm' | 'showDeliveryChallanManagement') => {
    switch (modal) {
      case 'showBillGenerator':
        setShowBillGenerator(true);
        break;
      case 'showCompanyManager':
        setShowCompanyManager(true);
        break;
      case 'showPaymentForm':
        setShowPaymentForm(true);
        break;
      case 'showUtReportForm':
        setShowUtReportForm(true);
        break;
      case 'showUtReport':
        setShowUtReport(true);
        break;
      case 'showDeliveryChallanForm':
        setShowDeliveryChallanForm(true);
        break;
      case 'showDeliveryChallanManagement':
        setShowDeliveryChallanManagement(true); // ✅ Fixed: using setter
        break;
    }
  }, []);

  const closeModal = useCallback((modal: 'showBillGenerator' | 'showCompanyManager' | 'showPaymentForm' | 'showUtReportForm' | 'showUtReport' | 'showDeliveryChallanForm' | 'showDeliveryChallanManagement') => {
    switch (modal) {
      case 'showBillGenerator':
        setShowBillGenerator(false);
        break;
      case 'showCompanyManager':
        setShowCompanyManager(false);
        setEditingCompany(null);
        break;
      case 'showPaymentForm':
        setShowPaymentForm(false);
        setSelectedCompanyForPayment(undefined);
        break;
      case 'showUtReportForm':
        setShowUtReportForm(false);
        break;
      case 'showUtReport':
        setShowUtReport(false);
        setSelectedUtReport(null);
        break;
      case 'showDeliveryChallanForm':
        setShowDeliveryChallanForm(false);
        break;
      case 'showDeliveryChallanManagement':
        setShowDeliveryChallanManagement(false); // ✅ Fixed: using setter
        setSelectedDeliveryChallan(null);
        break;
    }
  }, []);

  // Selection actions
  const selectQuotation = useCallback((quotation: QuotationRequest | null) => {
    setSelectedQuotation(quotation);
  }, []);

  const selectUtReport = useCallback((report: any | null) => {
    setSelectedUtReport(report);
  }, []);

  const selectCompanyForPayment = useCallback((companyId: string | undefined) => {
    setSelectedCompanyForPayment(companyId);
  }, []);

  const selectCompanyForLedger = useCallback((companyId: string) => {
    setSelectedCompanyForLedger(companyId);
  }, []);

  const selectCompanyDetails = useCallback((companyId: string | null) => {
    setDetailsCompanyId(companyId);
  }, []);

  const editCompany = useCallback((company: Company | null) => {
    setEditingCompany(company);
  }, []);

  const selectDeliveryChallan = useCallback((challan: DeliveryChallan | null) => {
    setSelectedDeliveryChallan(challan);
  }, []);

  // Business actions

  const saveBill = useCallback(async (billData: BillData) => {
    setIsSavingBill(true);
    try {
      // Generate invoice number if not provided
      let finalBillData = billData;
      if (!billData.invoiceNumber || billData.invoiceNumber.trim() === '') {
        console.log('saveBill: Generating invoice number for client:', billData.clientId);
        const { generateNextInvoiceNumber } = await import('../services/firebaseBilling');
        const invoiceNumber = await generateNextInvoiceNumber(billData.clientId);
        console.log('saveBill: Generated invoice number:', invoiceNumber);
        finalBillData = {
          ...billData,
          invoiceNumber: invoiceNumber
        };
      } else {
        console.log('saveBill: Using existing invoice number:', billData.invoiceNumber);
      }

      console.log('saveBill: Saving bill with invoice number:', finalBillData.invoiceNumber);
      const billId = await createBill(finalBillData);
      // Update the bill in local state with the Firestore-generated ID
      if (!finalBillData.id) {
        setBills(prev => prev.map(bill =>
          bill === finalBillData ? { ...bill, id: billId } : bill
        ));
      }
      await refreshData();
      setShowBillGenerator(false);
    } catch (error) {
      console.error('Error saving bill:', error);
      throw error;
    } finally {
      setIsSavingBill(false);
    }
  }, [refreshData]);

  const updateBill = useCallback(async (billData: BillData) => {
    setIsSavingBill(true);
    try {
      console.log('Updating bill:', billData);
      await firebaseUpdateBill(billData);
      await refreshData();
    } catch (error) {
      console.error('Error updating bill:', error);
      throw error;
    } finally {
      setIsSavingBill(false);
    }
  }, [refreshData]);

  const deleteBill = useCallback(async (billId: string) => {
    try {
      console.log('Deleting bill:', billId);
      await firebaseDeleteBill(billId);
      await refreshData();
    } catch (error) {
      console.error('Error deleting bill:', error);
      throw error;
    }
  }, [refreshData]);

  // Payment management functions
  const addPayment = useCallback(async (payment: Omit<PaymentRecord, 'id'>) => {
    try {
      console.log('Adding payment:', payment);
      await recordPayment(payment);
      await refreshData();
    } catch (error) {
      console.error('Error adding payment:', error);
      throw error;
    }
  }, [refreshData]);

  const updatePayment = useCallback(async (payment: PaymentRecord) => {
    try {
      console.log('Updating payment:', payment);
      await firebaseUpdatePayment(payment);
      await refreshData();
    } catch (error) {
      console.error('Error updating payment:', error);
      throw error;
    }
  }, [refreshData]);

  const deletePayment = useCallback(async (paymentId: string) => {
    try {
      console.log('Deleting payment:', paymentId);
      await firebaseDeletePayment(paymentId);
      await refreshData();
    } catch (error) {
      console.error('Error deleting payment:', error);
      throw error;
    }
  }, [refreshData]);

  const saveDeliveryChallan = useCallback(async (challan: Omit<DeliveryChallan, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      console.log('Creating delivery challan:', challan);
      // Generate challan number if not provided
      let finalChallan = challan;
      if (!challan.challanNumber || challan.challanNumber.trim() === '') {
        const challanNumber = await generateNextChallanNumber();
        finalChallan = {
          ...challan,
          challanNumber: challanNumber
        };
      }

      const challanId = await createDeliveryChallan(finalChallan);
      await refreshData();
      setShowDeliveryChallanForm(false);
    } catch (error) {
      console.error('Error saving delivery challan:', error);
      throw error;
    }
  }, [refreshData]);

  const updateDeliveryChallanAction = useCallback(async (challan: DeliveryChallan) => {
    try {
      console.log('Updating delivery challan:', challan);
      await updateDeliveryChallan(challan);
      await refreshData();
    } catch (error) {
      console.error('Error updating delivery challan:', error);
      throw error;
    }
  }, [refreshData]);

  const deleteDeliveryChallanAction = useCallback(async (challanId: string) => {
    try {
      console.log('Deleting delivery challan:', challanId);
      await deleteDeliveryChallan(challanId);
      await refreshData();
    } catch (error) {
      console.error('Error deleting delivery challan:', error);
      throw error;
    }
  }, [refreshData]);

  const markDeliveryChallanAsReturned = useCallback(async (challanId: string, returnCondition?: string, notes?: string) => {
    try {
      console.log('Marking delivery challan as returned:', challanId);
      await markChallanAsReturned(challanId, returnCondition, notes);
      await refreshData();
    } catch (error) {
      console.error('Error marking challan as returned:', error);
      throw error;
    }
  }, [refreshData]);

  const markDeliveryChallanAsOverdue = useCallback(async (challanId: string) => {
    try {
      console.log('Marking delivery challan as overdue:', challanId);
      await markChallanAsOverdue(challanId);
      await refreshData();
    } catch (error) {
      console.error('Error marking challan as overdue:', error);
      throw error;
    }
  }, [refreshData]);

  const markDeliveryChallanAsLost = useCallback(async (challanId: string, notes?: string) => {
    try {
      console.log('Marking delivery challan as lost:', challanId);
      await markChallanAsLost(challanId, notes);
      await refreshData();
    } catch (error) {
      console.error('Error marking challan as lost:', error);
      throw error;
    }
  }, [refreshData]);

  const saveCompany = useCallback(async (company: Company) => {
    setIsSavingCompany(true);
    try {
      await createCompany(company);
      await refreshData();
    } catch (error) {
      console.error('Error saving company:', error);
      throw error;
    } finally {
      setIsSavingCompany(false);
    }
  }, [refreshData]);

  const updateCompany = useCallback(async (company: Company) => {
    setIsSavingCompany(true);
    try {
      // ⚠️ Important: Avoid naming conflict with function name
      // The function parameter is `company`, and the Firebase function is also `updateCompany`
      // This will cause recursion! You need to rename the Firebase import or use a different approach.
      // For now, assuming the Firebase function is correctly imported as `firebaseUpdateCompany`
      // But since it's not in your imports, we'll assume you meant to call a different function.
      // If `updateCompany` is not defined in firebaseBilling, this will break.
      
      // Since your imports don't include `updateCompany`, this is likely an error.
      // You should implement or import the actual update function.
      // For now, we'll comment this out or throw an error.
      throw new Error('updateCompany function not implemented in firebaseBilling');
      // await updateCompany(company); // ❌ This would call itself recursively!
      await refreshData();
    } catch (error) {
      console.error('Error updating company:', error);
      throw error;
    } finally {
      setIsSavingCompany(false);
    }
  }, [refreshData]);

  const deleteCompany = useCallback(async (companyId: string) => {
    try {
      // Same issue: `deleteCompany` is not imported from firebaseBilling
      throw new Error('deleteCompany function not implemented in firebaseBilling');
      // await deleteCompany(companyId); // ❌ Would call itself
      await refreshData();
    } catch (error) {
      console.error('Error deleting company:', error);
      throw error;
    }
  }, [refreshData]);

  const savePayment = useCallback(async (payment: PaymentRecord) => {
    setIsSavingPayment(true);
    try {
      const { id, ...paymentData } = payment;
      await recordPayment(paymentData);
      await refreshData();
      setShowPaymentForm(false);
      setSelectedCompanyForPayment(undefined);
    } catch (error) {
      console.error('Error saving payment:', error);
      throw error;
    } finally {
      setIsSavingPayment(false);
    }
  }, [refreshData]);

  const saveUtReport = useCallback(async (report: any) => {
    setIsSavingUtReport(true);
    try {
      // Mock implementation - replace with actual API call
      console.log('Saving UT report:', report);
      setShowUtReportForm(false);
    } catch (error) {
      console.error('Error saving UT report:', error);
      throw error;
    } finally {
      setIsSavingUtReport(false);
    }
  }, []);


  return {
    // State
    quotations,
    bills,
    companies,
    payments,
    deliveryChallans,
    isLoading,
    activeTab,
    filterStatus,

    // Bill filtering
    billDateFrom,
    billDateTo,
    billStatusFilter,

    showBillGenerator,
    showCompanyManager,
    showPaymentForm,
    showUtReportForm,
    showUtReport,
    showDeliveryChallanForm,
    showDeliveryChallanManagement,
    selectedQuotation,
    selectedUtReport,
    selectedCompanyForPayment,
    selectedCompanyForLedger,
    detailsCompanyId,
    editingCompany,
    selectedDeliveryChallan,
    analyticsRange,
    analyticsCompany,
    ledgerSummary,
    isGeneratingPDF,
    isSavingBill,
    isSavingCompany,
    isSavingPayment,
    isSavingUtReport,
    
    // Actions
    loadData,
    refreshData,
    setActiveTab,
    setFilterStatus,
    openModal,
    closeModal,
    selectQuotation,
    selectUtReport,
    selectCompanyForPayment,
    selectCompanyForLedger,
    selectCompanyDetails,
    editCompany,
    selectDeliveryChallan,
    saveBill,
    updateBill,
    deleteBill,
    saveCompany,
    updateCompany,
    deleteCompany,
    savePayment,
    addPayment,
    saveDeliveryChallan,
    updateDeliveryChallanAction,
    deleteDeliveryChallanAction,
    markDeliveryChallanAsReturned,
    markDeliveryChallanAsOverdue,
    markDeliveryChallanAsLost,
    updatePayment,
    deletePayment,
    saveUtReport,
    setAnalyticsRange,
    setAnalyticsCompany,
    computeAnalyticsData,
    setBillDateFrom,
    setBillDateTo,
    setBillStatusFilter,
    getFilteredBills,
  };
};

export default useAdminState;